import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {ConfirmationModal, Icon, ListLazy, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {getThemeColor} from 'utils'
import {TreeFototreeCommentResponse} from 'types'
import {FototreeGlobalChatProps} from './FototreeGlobalChatProps'
import FototreeGlobalChatInput from './FototreeGlobalChatInput'
import FototreeGlobalChatItem from './FototreeGlobalChatItem'

const StyledContainer = styled.div`
  position: fixed;
  background-color: ${({theme}) => getThemeColor(theme, 'black', 0.95)};
  height: 100%;
  width: 100%;
  max-width: ${convertUnit(596)};
  z-index: 4;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
`

const StyledPreviewContainer = styled.div`
  position: fixed;
  background-color: ${({theme}) => getThemeColor(theme, 'black', 0.5)};
  width: calc(100% - ${convertUnit(40)});
  max-width: calc((2 / 3) * ${convertUnit(596)});
  border-radius: ${convertUnit(8)};
  bottom: ${convertUnit(78)};
  padding: ${convertUnit(4)} 0;
  cursor: pointer;
`

const StyledContentContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
`

const StyledEmptyContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
`

const StyledIconBackWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: ${convertUnit(42)};
  height: ${convertUnit(42)};
  padding: ${convertUnit(20)};
  cursor: pointer;
`

export default function FototreeGlobalChat({
  stateData,
  stateOverlay,
  stateLoginModal,
  userType,
  treeId,
  isUserScroll,
}: FototreeGlobalChatProps) {
  const {translate} = useTranslation()
  const stateDeleteModal = useState(false)
  const [deleteModal, setDeleteModal] = stateDeleteModal
  const stateSelectedComment = useState('')
  const selectedComment = stateSelectedComment[0]
  const stateSearch = useState(0)
  const [search, setSearch] = stateSearch
  const [overlay, setOverlay] = stateOverlay
  const data = stateData[0]

  const handleLoadData = useCallback(
    async (page: number, limit: number) => {
      if (isUserScroll && overlay === 'hidden') return
      const response = await requestData('tree_get_fototree_comment', {
        useDefaultMessage: true,
        params: {
          page,
          limit,
          tree_id: treeId,
        },
      })
      return typeof response !== 'string' && response.status === 200
        ? response.data.result
        : []
    },
    [isUserScroll, overlay, treeId],
  )

  const handleDeleteComment = useCallback(() => {
    requestData('tree_delete_fototree_comment', {
      useDefaultMessage: true,
      data: {id: selectedComment},
      onRequestSuccess: ({status}) => {
        if (status === 200) setSearch(new Date().getTime())
      },
    })
  }, [selectedComment, setSearch])

  const handleRenderDeleteConfirmation = useMemo(
    () => (
      <ConfirmationModal
        visible={deleteModal}
        cancelText={translate('global:cancel')}
        confirmText={translate('global:yesImSure')}
        title={translate('forkygram:deletePostComment')}
        text={translate('forkygram:deletePostCommentMessage')}
        onConfirm={handleDeleteComment}
        toggleModal={() => setDeleteModal((prev) => !prev)}
      />
    ),
    [deleteModal, handleDeleteComment, setDeleteModal, translate],
  )

  const handleRenderItem = useCallback(
    (item: TreeFototreeCommentResponse) => (
      <FototreeGlobalChatItem
        data={item}
        overlay={overlay}
        userType={userType}
        stateDeleteModal={stateDeleteModal}
        stateSelectedComment={stateSelectedComment}
      />
    ),
    [overlay, stateDeleteModal, stateSelectedComment, userType],
  )

  const handleRenderList = useMemo(
    () => (
      <ListLazy
        scrollbar={false}
        keyExtractor={(item) => item.id}
        stateData={stateData}
        renderItem={handleRenderItem}
        loadData={handleLoadData}
        search={search}
        listEmptyElement={
          <StyledEmptyContainer>
            <Paragraph color="gray_6">
              {translate('giftShop:emptyDefaultMessage')}
            </Paragraph>
          </StyledEmptyContainer>
        }
      />
    ),
    [handleLoadData, handleRenderItem, search, stateData, translate],
  )

  const handleRenderInputBox = useMemo(
    () => (
      <FototreeGlobalChatInput
        treeId={treeId}
        stateSearch={stateSearch}
        stateLoginModal={stateLoginModal}
      />
    ),
    [stateLoginModal, stateSearch, treeId],
  )

  const handleRenderBack = useMemo(
    () => (
      <StyledIconBackWrapper
        onClick={() => setOverlay(isUserScroll ? 'hidden' : 'preview')}>
        <Icon type="back" size={convertUnit(20)} color="white_1" />,
      </StyledIconBackWrapper>
    ),
    [isUserScroll, setOverlay],
  )

  const handleRenderContent = useMemo(() => {
    switch (overlay) {
      case 'preview':
        return (
          <StyledPreviewContainer onClick={() => setOverlay('full')}>
            {data.slice(0, 2).map((item) => (
              <FototreeGlobalChatItem
                key={item.id}
                data={item}
                overlay={overlay}
              />
            ))}
          </StyledPreviewContainer>
        )
      case 'full':
        return (
          <StyledContainer>
            <StyledContentContainer>
              {handleRenderBack}
              {handleRenderList}
              {handleRenderInputBox}
            </StyledContentContainer>
          </StyledContainer>
        )
      default:
        return handleRenderList
    }
  }, [
    data,
    handleRenderBack,
    handleRenderInputBox,
    handleRenderList,
    overlay,
    setOverlay,
  ])

  return (
    <>
      {handleRenderDeleteConfirmation}
      {handleRenderContent}
    </>
  )
}
